import { useState, useEffect, useCallback } from 'react'
import { audioService } from '../services/audioService'
import { AudioState, PlaybackState } from '../types/AudioState'

export const useAudio = () => {
  const [playbackState, setPlaybackState] = useState<PlaybackState>({})
  const [isInitialized, setIsInitialized] = useState(false)

  const updatePlaybackState = useCallback((soundPath: string, updates: Partial<AudioState>) => {
    setPlaybackState(prev => {
      const currentState = prev[soundPath] || {
        isPlaying: false,
        currentSound: null,
        error: null,
        isLoading: false
      }

      return {
        ...prev,
        [soundPath]: {
          ...currentState,
          ...updates
        }
      }
    })
  }, [])

  // Initialize audio service
  useEffect(() => {
    const initAudio = async () => {
      try {
        // Audio context will be initialized on first user interaction
        setIsInitialized(true)
        console.log('Audio hook initialized')
      } catch (error) {
        console.error('Failed to initialize audio:', error)
      }
    }

    // Listen for audio ended events
    const handleAudioEnded = (event: CustomEvent) => {
      const { soundPath } = event.detail
      updatePlaybackState(soundPath, {
        isPlaying: false,
        currentSound: null,
        isLoading: false,
        error: null
      })
    }

    window.addEventListener('audioEnded', handleAudioEnded as EventListener)

    initAudio()

    return () => {
      window.removeEventListener('audioEnded', handleAudioEnded as EventListener)
    }
  }, [updatePlaybackState])

  const playSound = useCallback(async (soundPath: string, soundData?: any) => {
    try {
      // Set loading state
      updatePlaybackState(soundPath, { isLoading: true, error: null })

      // Play the sound
      await audioService.playSound(soundPath, soundData)

      // Update state to playing
      updatePlaybackState(soundPath, {
        isPlaying: true,
        currentSound: soundPath,
        isLoading: false,
        error: null
      })

      console.log('Sound started playing:', soundPath)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      updatePlaybackState(soundPath, {
        isPlaying: false,
        currentSound: null,
        isLoading: false,
        error: errorMessage
      })
      console.error('Failed to play sound:', soundPath, error)
    }
  }, [updatePlaybackState])

  const stopSound = useCallback((soundPath: string) => {
    try {
      audioService.stopSound(soundPath)
      updatePlaybackState(soundPath, {
        isPlaying: false,
        currentSound: null,
        error: null
      })
      console.log('Sound stopped:', soundPath)
    } catch (error) {
      console.error('Failed to stop sound:', soundPath, error)
    }
  }, [updatePlaybackState])

  const stopAllSounds = useCallback(() => {
    try {
      audioService.stopAllSounds()
      setPlaybackState({})
      console.log('All sounds stopped')
    } catch (error) {
      console.error('Failed to stop all sounds:', error)
    }
  }, [])

  const getAudioState = useCallback((soundPath: string): AudioState => {
    return playbackState[soundPath] || {
      isPlaying: false,
      currentSound: null,
      error: null,
      isLoading: false
    }
  }, [playbackState])

  const isSoundPlaying = useCallback((soundPath: string): boolean => {
    return getAudioState(soundPath).isPlaying
  }, [getAudioState])

  return {
    isInitialized,
    playSound,
    stopSound,
    stopAllSounds,
    getAudioState,
    isSoundPlaying,
    playbackState
  }
}
